/**
 * Vastuumatriisi cell resolution. Mirrors step 3 of the backend vastuuhenkilö
 * resolver (`TaskService._location_responsible_candidates`): walk up from the
 * sijainti, nearest wins, and at the same sijainti a `section_locations` value
 * beats a vastuumatriisi rule. Falls back to the työvaihe default.
 */

import type { Location } from "@/types";

export interface MatrixSection {
  sectionId: string;
  code: string | null;
  name: string | null;
  plotId: string | null;
  headsectionNumber: number | null;
  headsectionLabel: string | null;
  defaultResponsibleUserId: string | null;
}

export interface MatrixEntry {
  locationId: string;
  sectionId: string;
  responsibleUserId: string;
}

export interface MatrixData {
  sections: MatrixSection[];
  rules: MatrixEntry[];
  sectionLocationResponsibles: MatrixEntry[];
}

export type CellSource = "set" | "inherited" | "default" | "none";

export interface CellState {
  userId: string | null;
  source: CellSource;
  heldBySectionLocation: boolean;
  ruleUserIdHere: string | null;
  exceptions: { locationId: string; responsibleUserId: string }[];
}

export interface MatrixIndex {
  parentOf: Map<string, string | null>;
  childrenOf: Map<string, string[]>;
  rules: Map<string, string>;
  sectionLocations: Map<string, string>;
  defaults: Map<string, string | null>;
}

export const cellKey = (locationId: string, sectionId: string) => `${locationId}|${sectionId}`;

export function buildMatrixIndex(data: MatrixData, locations: Location[]): MatrixIndex {
  const ids = new Set(locations.map((l) => l.id));
  const parentOf = new Map<string, string | null>();
  const childrenOf = new Map<string, string[]>();
  for (const l of locations) {
    const parent = l.parentLocationId && ids.has(l.parentLocationId) ? l.parentLocationId : null;
    parentOf.set(l.id, parent);
    if (parent) childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), l.id]);
  }
  const toMap = (entries: MatrixEntry[]) =>
    new Map(entries.map((e) => [cellKey(e.locationId, e.sectionId), e.responsibleUserId]));
  return {
    parentOf,
    childrenOf,
    rules: toMap(data.rules),
    sectionLocations: toMap(data.sectionLocationResponsibles),
    defaults: new Map(data.sections.map((s) => [s.sectionId, s.defaultResponsibleUserId])),
  };
}

function valueAt(index: MatrixIndex, locationId: string, sectionId: string): string | undefined {
  const key = cellKey(locationId, sectionId);
  return index.sectionLocations.get(key) ?? index.rules.get(key);
}

export function resolveCell(index: MatrixIndex, locationId: string, sectionId: string): CellState {
  const key = cellKey(locationId, sectionId);
  let userId: string | null = null;
  let source: CellSource = "none";
  for (let cur: string | null = locationId; cur; cur = index.parentOf.get(cur) ?? null) {
    const hit = valueAt(index, cur, sectionId);
    if (hit) {
      userId = hit;
      source = cur === locationId ? "set" : "inherited";
      break;
    }
  }
  if (!userId) {
    userId = index.defaults.get(sectionId) ?? null;
    source = userId ? "default" : "none";
  }

  const exceptions: CellState["exceptions"] = [];
  const stack = [...(index.childrenOf.get(locationId) ?? [])];
  while (stack.length) {
    const d = stack.pop()!;
    const hit = valueAt(index, d, sectionId);
    if (hit && hit !== userId) exceptions.push({ locationId: d, responsibleUserId: hit });
    stack.push(...(index.childrenOf.get(d) ?? []));
  }

  return {
    userId,
    source,
    heldBySectionLocation: index.sectionLocations.has(key),
    ruleUserIdHere: index.rules.get(key) ?? null,
    exceptions,
  };
}

export interface MatrixUserName {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}

/**
 * Label for a cell whose subtree holds more than one vastuuhenkilö, e.g.
 * "Jaettu: Jason ja Erbert". First names only; a surname initial is added
 * when two listed people share a first name. More than three people
 * collapse to "Jaettu: A, B ja N muuta".
 */
export function sharedCellLabel(userIds: string[], users: MatrixUserName[]): string {
  const listed = userIds.map((id) => users.find((u) => u.id === id));
  const firstNames = listed.map((u) => u?.firstName?.trim() ?? "");
  const names = listed.map((u, i) => {
    if (!u) return "–";
    const first = firstNames[i];
    const full = `${first} ${u.lastName?.trim() ?? ""}`.trim() || u.email;
    if (!first) return full;
    const collides = firstNames.some((f, j) => j !== i && f.toLowerCase() === first.toLowerCase());
    const initial = u.lastName?.trim().charAt(0);
    return collides && initial ? `${first} ${initial}.` : first;
  });
  if (names.length > 3) return `Jaettu: ${names[0]}, ${names[1]} ja ${names.length - 2} muuta`;
  if (names.length === 1) return names[0];
  return `Jaettu: ${names.slice(0, -1).join(", ")} ja ${names[names.length - 1]}`;
}
